import { useState, useRef, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { describeCompletionBlock } from '@/utils/ticketWorkLifecycle';
import { toast } from 'sonner';
import { useEveWakeWord } from './useEveWakeWord';
// ARC-A4: route the user-id input through AuthContext rather than
// owning a duplicate `supabase.auth.onAuthStateChange` listener inside
// this hook. Pre-A4 the listener fired on every auth event
// (TOKEN_REFRESHED, USER_UPDATED, etc.) and unconditionally re-fetched
// `profiles.display_name` — but display_name only changes when the
// signed-in user changes (tokens refresh keeps the same user). Post-A4
// the fetch is keyed on `user?.id` from useAuth(); EveVoiceShell only
// renders for `isAdmin` (a signed-in admin), so this hook always
// mounts inside AuthProvider.
import { useAuth } from '@/contexts/AuthContext';
/// <reference path="../types/speech-recognition.d.ts" />

function generateId(): string {
  return crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ---------- Types ----------

export type EveState =
  | 'listening'
  | 'greeting'
  | 'recording'
  | 'processing'
  | 'speaking'
  | 'disabled'
  | 'error';

interface EveResponse {
  audioBase64: string;
  transcript: string;
  intent: string;
  status: string;
  message: string;
  pendingActionId?: string;
  requires_client_action?: string;
  ticket_id?: string;
}

interface UseEveVoiceOptions {
  ticketId?: string;
  shopId?: string;
  enabled?: boolean;
}

// ---------- Constants ----------

const GREETING_POOL = [
  'Yes {name}?',
  'Go ahead {name}.',
  "I'm listening.",
  'What can I help with?',
  'Ready.',
];

const SILENCE_THRESHOLD = 0.01; // RMS below this = silence
const SILENCE_DURATION_MS = 2_500;
const MAX_RECORDING_MS = 8_000;
const NO_SPEECH_TIMEOUT_MS = 5_000;
const RMS_POLL_INTERVAL_MS = 200;

// ---------- Helpers ----------

function pickGreeting(name?: string | null): string {
  const pool = GREETING_POOL;
  const text = pool[Math.floor(Math.random() * pool.length)];
  return text.replace('{name}', name || '').replace(/\s+/g, ' ').trim();
}

// ---------- WAV Conversion ----------

async function blobToWav(blob: Blob): Promise<Blob> {
  const arrayBuffer = await blob.arrayBuffer();
  const audioCtx = new OfflineAudioContext(1, 1, 16000);
  let audioBuffer: AudioBuffer;
  try {
    audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
  } catch {
    // If decoding fails, return raw blob (server will handle)
    console.warn('[useEveVoice] Could not decode audio for WAV conversion, sending raw');
    return blob;
  }

  // Resample to 16kHz mono
  const targetSampleRate = 16000;
  const numSamples = Math.ceil(audioBuffer.duration * targetSampleRate);
  const offlineCtx = new OfflineAudioContext(1, numSamples, targetSampleRate);
  const source = offlineCtx.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(offlineCtx.destination);
  source.start(0);
  const rendered = await offlineCtx.startRendering();

  // Encode as 16-bit PCM WAV
  const pcmData = rendered.getChannelData(0);
  const wavBuffer = encodeWav(pcmData, targetSampleRate);
  return new Blob([wavBuffer], { type: 'audio/wav' });
}

function encodeWav(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const numSamples = samples.length;
  const bitsPerSample = 16;
  const numChannels = 1;
  const byteRate = sampleRate * numChannels * (bitsPerSample / 8);
  const blockAlign = numChannels * (bitsPerSample / 8);
  const dataSize = numSamples * (bitsPerSample / 8);
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < numSamples; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    offset += 2;
  }

  return buffer;
}

// ---------- Hook ----------

export function useEveVoice({ ticketId, shopId, enabled = true }: UseEveVoiceOptions = {}) {
  const [state, setState] = useState<EveState>('disabled');
  const [lastTranscript, setLastTranscript] = useState('');
  const [lastMessage, setLastMessage] = useState('');
  const [pendingActionId, setPendingActionId] = useState<string | null>(null);

  const displayNameRef = useRef<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const silenceStartRef = useRef<number | null>(null);
  const speechDetectedRef = useRef(false);
  const rmsTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const noSpeechTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionIdRef = useRef(generateId());
  const streamRef = useRef<MediaStream | null>(null);
  const greetAbortRef = useRef<AbortController | null>(null);
  const userIdRef = useRef<string | null>(null);
  const startRecordingRef = useRef<(wakeTime: string) => Promise<void>>();

  // ARC-A4: user-id source is now AuthContext (not a duplicate
  // onAuthStateChange listener). The fetch fires on mount + whenever
  // user.id changes — which is when display_name needs to be re-pulled
  // anyway. Pre-A4's redundant refetches on TOKEN_REFRESHED /
  // USER_UPDATED events (where user.id is unchanged) are eliminated.
  const { user: authUser } = useAuth();

  // ----- Fetch display name -----
  const fetchDisplayName = useCallback(async () => {
    try {
      if (!authUser?.id) {
        userIdRef.current = null;
        displayNameRef.current = null;
        return;
      }
      userIdRef.current = authUser.id;
      const { data } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', authUser.id)
        .maybeSingle();
      displayNameRef.current = data?.display_name || null;
    } catch {
      // non-fatal
    }
  }, [authUser?.id]);

  useEffect(() => {
    fetchDisplayName();
  }, [fetchDisplayName]);

  // ----- Resource cleanup -----
  const cleanupMedia = useCallback(() => {
    if (rmsTimerRef.current) {
      clearInterval(rmsTimerRef.current);
      rmsTimerRef.current = null;
    }
    if (maxTimerRef.current) {
      clearTimeout(maxTimerRef.current);
      maxTimerRef.current = null;
    }
    if (noSpeechTimerRef.current) {
      clearTimeout(noSpeechTimerRef.current);
      noSpeechTimerRef.current = null;
    }
    if (mediaRecorderRef.current?.state === 'recording') {
      try { mediaRecorderRef.current.stop(); } catch { /* ignore */ }
    }
    mediaRecorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioContextRef.current?.state !== 'closed') {
      try { audioContextRef.current?.close(); } catch { /* ignore */ }
    }
    audioContextRef.current = null;
    analyserRef.current = null;
    window.speechSynthesis?.cancel();
  }, []);

  // ----- Log interaction -----
  const logInteraction = useCallback(async (fields: {
    wakeDetectedAt?: string;
    greetingText?: string;
    commandTranscript?: string;
    parsedIntent?: string;
    executionResult?: string;
    errorReason?: string;
  }) => {
    try {
      await supabase.from('eve_interaction_logs').insert({
        session_id: sessionIdRef.current,
        user_id: userIdRef.current!,
        shop_id: shopId || null,
        wake_detected_at: fields.wakeDetectedAt || null,
        greeting_text: fields.greetingText || null,
        command_transcript: fields.commandTranscript || null,
        parsed_intent: fields.parsedIntent || null,
        execution_result: fields.executionResult || null,
        error_reason: fields.errorReason || null,
      } as any);
    } catch {
      // non-fatal logging
    }
  }, [shopId]);

  // ----- Send audio to edge function -----
  const sendAudio = useCallback(async (audioBlob: Blob, wakeTime: string) => {
    setState('processing');
    try {
      // Convert to WAV for reliable Azure STT
      const wavBlob = await blobToWav(audioBlob);

      const arrayBuffer = await wavBlob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const audioBase64 = btoa(binary);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Not authenticated');
        setState('listening');
        return;
      }

      // Route through supabase.functions.invoke() — the canonical edge
      // call path used everywhere else in this app (cascadeTrigger,
      // ticketTimerManager, ticketMerging, autoSmartAssign, ocisIngest).
      // The hand-rolled fetch + manual headers pre-fix produced the
      // Chrome error: "Failed to construct 'Request': 'headers' of
      // 'RequestInit' (Argument 2) is not a valid ByteString" whenever a
      // transient between auth-refresh / HMR left one of the
      // Authorization / apikey values outside the Latin-1 ByteString
      // range. supabase-js owns header coercion (Authorization, apikey,
      // Content-Type) and has been hardened against this exact failure
      // mode.
      const { data, error } = await supabase.functions.invoke<EveResponse>(
        'eve-voice',
        {
          body: {
            audioBase64,
            ticketId,
            shopId,
            pendingActionId,
          },
        },
      );

      if (error) {
        throw new Error(error.message || 'EVE request failed');
      }
      if (!data) {
        throw new Error('EVE returned no data');
      }
      setLastTranscript(data.transcript);
      setLastMessage(data.message);

      // ══ VOICE TICKET COMPLETION IS NOT SUPPORTED ═════════════════════
      // There is deliberately no handler here.
      //
      // Until 8G.1.2 the edge function answered "stop timer" with
      // `requires_client_action: "stop_ticket_timer"`, and this block turned
      // that into a ticket COMPLETION attempt. Saying "stop timer" therefore
      // asked the operator where the vehicle had gone, and the edge function
      // had already emitted a `ticket_completed` event for a completion that
      // had not happened. Stopping your work and ending a customer's visit
      // are different acts; the edge function now performs only the first and
      // emits no completion directive at all.
      //
      // 8G.1.1 made this path SAFE — it called the canonical authority with a
      // null location and surfaced LOCATION_REQUIRED — but safe is not the
      // same as correct. The intent was never completion, so the right fix is
      // to stop inferring one, not to handle the inference politely.
      //
      // Adding voice completion later means a DISTINCT intent ("complete
      // ticket", "finish work") that performs ZERO timer mutation and opens
      // the same explicit location picker the ticket card uses. It is not
      // wired here because nothing consumes it yet, and a state value nobody
      // reads is not a flow — that is precisely what `completionNeedsLocation`
      // was, and it is removed with this comment.

      if (data.status === 'awaiting_input' && data.pendingActionId) {
        setPendingActionId(data.pendingActionId);
      } else if (data.status === 'pending_confirm' && data.pendingActionId) {
        setPendingActionId(data.pendingActionId);
      } else {
        setPendingActionId(null);
      }

      // Log
      logInteraction({
        wakeDetectedAt: wakeTime,
        commandTranscript: data.transcript,
        parsedIntent: data.intent,
        executionResult: data.status === 'error' ? 'error' : 'success',
      });

      // Play TTS response
      if (data.audioBase64) {
        setState('speaking');
        const isAwaitingInput = data.status === 'awaiting_input';
        try {
          const audioBytes = Uint8Array.from(atob(data.audioBase64), (c) => c.charCodeAt(0));
          const blob = new Blob([audioBytes], { type: 'audio/mpeg' });
          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);
          audioRef.current = audio;
          audio.onended = () => {
            URL.revokeObjectURL(url);
            if (isAwaitingInput) {
              startRecordingRef.current?.(wakeTime);
            } else {
              setState('listening');
            }
          };
          audio.onerror = () => {
            URL.revokeObjectURL(url);
            if (isAwaitingInput) {
              startRecordingRef.current?.(wakeTime);
            } else {
              setState('listening');
            }
          };
          await audio.play();
        } catch {
          if (data.status === 'awaiting_input') {
            startRecordingRef.current?.(wakeTime);
          } else {
            setState('listening');
          }
        }
      } else {
        if (data.status === 'awaiting_input') {
          startRecordingRef.current?.(wakeTime);
        } else {
          setState('listening');
        }
      }
    } catch (err) {
      console.error('[useEveVoice] Error:', err);
      toast.error(err instanceof Error ? err.message : 'EVE voice error');
      logInteraction({
        wakeDetectedAt: wakeTime,
        executionResult: 'error',
        errorReason: err instanceof Error ? err.message : 'unknown',
      });
      setState('listening');
    }
  }, [ticketId, shopId, pendingActionId, logInteraction]);

  // ----- Start recording with RMS silence detection -----
  const startRecording = useCallback(async (wakeTime: string) => {
    setState('recording');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, sampleRate: 16000 },
      });
      streamRef.current = stream;

      // AudioContext + Analyser for RMS
      const actx = new AudioContext();
      audioContextRef.current = actx;
      const source = actx.createMediaStreamSource(stream);
      const analyser = actx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      analyserRef.current = analyser;

      chunksRef.current = [];
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      const finishRecording = () => {
        if (rmsTimerRef.current) { clearInterval(rmsTimerRef.current); rmsTimerRef.current = null; }
        if (maxTimerRef.current) { clearTimeout(maxTimerRef.current); maxTimerRef.current = null; }
        if (noSpeechTimerRef.current) { clearTimeout(noSpeechTimerRef.current); noSpeechTimerRef.current = null; }
        if (recorder.state === 'recording') recorder.stop();
      };

      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (actx.state !== 'closed') {
          try { actx.close(); } catch { /* ignore */ }
        }
        audioContextRef.current = null;
        analyserRef.current = null;

        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size > 0 && speechDetectedRef.current) {
          sendAudio(blob, wakeTime);
        } else {
          // No command captured — accidental wake
          logInteraction({
            wakeDetectedAt: wakeTime,
            executionResult: 'no_command',
          });
          setState('listening');
        }
      };

      recorder.start(100);
      silenceStartRef.current = null;
      speechDetectedRef.current = false;

      // RMS polling
      const dataArray = new Float32Array(analyser.fftSize);
      rmsTimerRef.current = setInterval(() => {
        if (!analyserRef.current) return;
        analyserRef.current.getFloatTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) sum += dataArray[i] * dataArray[i];
        const rms = Math.sqrt(sum / dataArray.length);

        if (rms >= SILENCE_THRESHOLD) {
          speechDetectedRef.current = true;
          silenceStartRef.current = null;
        } else if (speechDetectedRef.current) {
          // Speech was detected, now silence
          if (!silenceStartRef.current) {
            silenceStartRef.current = Date.now();
          } else if (Date.now() - silenceStartRef.current >= SILENCE_DURATION_MS) {
            finishRecording();
          }
        }
      }, RMS_POLL_INTERVAL_MS);

      // Hard max recording time
      maxTimerRef.current = setTimeout(finishRecording, MAX_RECORDING_MS);

      // No speech timeout
      noSpeechTimerRef.current = setTimeout(() => {
        if (!speechDetectedRef.current) {
          finishRecording();
        }
      }, NO_SPEECH_TIMEOUT_MS);
    } catch (err) {
      console.error('[useEveVoice] Mic error:', err);
      toast.error('Microphone access denied');
      logInteraction({ wakeDetectedAt: wakeTime, executionResult: 'error', errorReason: 'mic_denied' });
      setState('listening');
    }
  }, [sendAudio, logInteraction]);

  // Keep ref in sync for use in sendAudio's awaiting_input flow
  startRecordingRef.current = startRecording;

  // ----- Greeting via Azure TTS (same voice as command responses) -----
  const speakGreeting = useCallback(async (wakeTime: string) => {
    setState('greeting');

    // Abort any previous in-flight greet request
    greetAbortRef.current?.abort();
    const abortCtrl = new AbortController();
    greetAbortRef.current = abortCtrl;

    const fallbackTimeout = setTimeout(() => {
      if (!abortCtrl.signal.aborted) {
        startRecording(wakeTime);
      }
    }, 3_000);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (abortCtrl.signal.aborted) { clearTimeout(fallbackTimeout); return; }

      if (!session?.access_token) {
        clearTimeout(fallbackTimeout);
        if (!abortCtrl.signal.aborted) startRecording(wakeTime);
        return;
      }

      // See sendAudio() — same rationale for routing through
      // supabase.functions.invoke() instead of a hand-rolled fetch.
      // FunctionInvokeOptions supports `signal`, so abort behavior is
      // preserved.
      const { data, error } = await supabase.functions.invoke<{
        audioBase64?: string;
        message: string;
      }>('eve-voice', {
        body: {
          action: 'greet',
          userName: displayNameRef.current || '',
        },
        signal: abortCtrl.signal,
      });

      if (abortCtrl.signal.aborted) { clearTimeout(fallbackTimeout); return; }
      if (error) throw new Error(error.message || 'Greet request failed');
      if (!data) throw new Error('Greet returned no data');

      clearTimeout(fallbackTimeout);
      if (abortCtrl.signal.aborted) return;

      logInteraction({ wakeDetectedAt: wakeTime, greetingText: data.message });
      setLastMessage(data.message);

      if (data.audioBase64) {
        const audioBytes = Uint8Array.from(atob(data.audioBase64), (c) => c.charCodeAt(0));
        const blob = new Blob([audioBytes], { type: 'audio/mpeg' });
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => {
          URL.revokeObjectURL(url);
          if (!abortCtrl.signal.aborted) startRecording(wakeTime);
        };
        audio.onerror = () => {
          URL.revokeObjectURL(url);
          if (!abortCtrl.signal.aborted) startRecording(wakeTime);
        };
        await audio.play();
      } else {
        if (!abortCtrl.signal.aborted) startRecording(wakeTime);
      }
    } catch (err) {
      clearTimeout(fallbackTimeout);
      if (!abortCtrl.signal.aborted) startRecording(wakeTime);
    }
  }, [startRecording, logInteraction]);

  // ----- Wake handler -----
  const onWake = useCallback(() => {
    if (state !== 'listening') return;
    const wakeTime = new Date().toISOString();
    sessionIdRef.current = generateId(); // new session per interaction
    speakGreeting(wakeTime);
  }, [state, speakGreeting]);

  // ----- Wake word integration -----
  const wakeWord = useEveWakeWord({
    onWake,
    enabled: enabled && state === 'listening',
  });


  // Initialize state
  useEffect(() => {
    if (enabled) {
      setState('listening');
    } else {
      setState('disabled');
      cleanupMedia();
    }
    return () => {
      cleanupMedia();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Map wake word errors to our state — auto-recover when error clears
  useEffect(() => {
    if (wakeWord.error) {
      setState('error');
    } else if (state === 'error') {
      setState('listening');
    }
  }, [wakeWord.error, state]);

  // ----- Cancel / reset to listening -----
  const cancel = useCallback(() => {
    greetAbortRef.current?.abort();
    greetAbortRef.current = null;
    cleanupMedia();
    sessionIdRef.current = generateId();
    chunksRef.current = [];
    silenceStartRef.current = null;
    speechDetectedRef.current = false;
    setPendingActionId(null);
    setLastMessage('');
    setLastTranscript('');
    setState('listening');

    // Use dedicated restart to clear cooldown and ensure fresh wake-word engine
    wakeWord.restart();
  }, [cleanupMedia, wakeWord]);

  return {
    state,
    error: wakeWord.error,
    lastTranscript,
    lastMessage,
    pendingActionId,
    cancel,
    isListening: state === 'listening',
    isRecording: state === 'recording',
    isProcessing: state === 'processing',
    isSpeaking: state === 'speaking',
  };
}
